import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import { followUpSurveyListTypeMap, IFollowUpSurveyForList } from '@nkr/models/index';
import { FollowUpSurveyListType } from '@nkr/enums/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ActionButtonRenderer } from './action-button-renderer';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColumnApi, FirstDataRenderedEvent, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { SurveyAction } from './survey-action';
import { Label } from 'react-bootstrap';
import { FollowUpStatus } from '@nkr/enums/follow-up-status';
import { Utility } from '@nkr/services/utility';

interface IProps {
    list: IFollowUpSurveyForList[],
    listType: FollowUpSurveyListType,
    centerId: number,
    isAdmin: boolean,
    isAdminLogin: boolean,
    isLoading: boolean,
    onLoadingComplete: () => void,
    onGridReady: (e: GridReadyEvent) => void,
    onItemSelected: (id: number, action: SurveyAction) => void
}

const CommonList = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        if (props.listType != null) {
            e.api.sizeColumnsToFit();
            props.onLoadingComplete();
        }
    };

    const onBodyScroll = () => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const getSortKey = (data: IFollowUpSurveyForList) => {                    
        let isCompleted = data.statusId >= FollowUpStatus.Complete && data.statusId < FollowUpStatus.Incomplete;
        let key = "";
        
        if (data.statusId == FollowUpStatus.DirectPendingAppointment) 
            key = "c";                   
        else if (data.statusId == FollowUpStatus.QuestPendingLabOrder || data.statusId == FollowUpStatus.PhlebotPendingRequisition) 
            key = "d";                   
        else if (isCompleted && data.areLabResultsGood == false && data.isNotifiedOfLabResults == false) 
            key = "a";                   
        else if (data.hasRegrets != null && data.isReviewed == false) 
            key = "b";                        
        else 
            key = "e";        

        return key;
    }

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const getListTypeName = () => {
        let listType = followUpSurveyListTypeMap.find(x => x.type == props.listType);

        if (listType != null) {
            return listType.name;
        }

        return '';
    };

    const cols: ColDef[] = [
        {
            colId: 'rowNumber',
            headerName: '#',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>#</SortableHeaderComponent>,
            width: 55,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.rowIndex + 1}</>,
            suppressSizeToFit: true
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            hide: props.isAdmin == false,
            comparator: Utility.nameComparator
        },
        {
            colId: 'firstName',
            field: 'firstName',
            headerName: 'First Name',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true
        },
        {
            colId: 'lastName',
            field: 'lastName',
            headerName: 'Last Name',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true
        },
        {
            colId: 'birthDate',
            field: 'birthDate',
            headerName: 'Birth Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Birth Date</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'city',
            field: 'city',
            headerName: 'City',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>City</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true
        },
        {
            colId: 'state',
            field: 'state',
            headerName: 'State',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>State</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true
        },
        {
            colId: 'surveyInitiatedDateTime',
            field: 'surveyInitiatedDateTime',
            headerName: 'Donated',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Initiated</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'donationDate',
            field: 'donationDate',
            headerName: 'Donated',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donated</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'surveyInstance',
            field: 'surveyInstance',
            headerName: 'Survey',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Survey</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            comparator: (valueA, valueB, nodeA, nodeB) => nodeA.data.surveyInstanceId - nodeB.data.surveyInstanceId
        },
        {
            colId: 'actionRequired',
            headerName: 'Action Required',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Action Required</SortableHeaderComponent>,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                let a = getSortKey(nodeA.data);
                let b = getSortKey(nodeB.data);
                return a.localeCompare(b);               
            },
            suppressSizeToFit: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IFollowUpSurveyForList;
                let isCompleted = data.statusId >= FollowUpStatus.Complete && data.statusId < FollowUpStatus.Incomplete;

                if (data.statusId == FollowUpStatus.DirectPendingAppointment) {
                    return (
                        <span style={{ padding: 4.2 }} className='label label-full label-default' title='This donor requested to come directly to the transplant center'>Schedule Appt</span>
                    );
                }

                if (data.statusId == FollowUpStatus.QuestPendingLabOrder || data.statusId == FollowUpStatus.PhlebotPendingRequisition) {
                    return (
                        <span style={{ padding: 4.2 }} className='label label-full label-warning' title='This donor completed the survey but still needs lab orders'>Upload Lab Orders</span>
                    );
                }

                if (isCompleted && data.areLabResultsGood == false && data.isNotifiedOfLabResults == false) {
                    return (
                        <span style={{ padding: 4.2 }} className='label label-full label-info' title='This donor has completed results but they were abnormal and the donor has not yet been marked as notified'>Notify Donor</span>
                    );
                }

                if (data.hasRegrets != null && data.isReviewed == false) {
                    return (
                        <span style={{ padding: 4.2 }} className='label label-full label-success' title='This donor has completed the survey, which has not yet been marked as reviewed.'>Review Survey</span>
                    );
                }

                return (
                    <>{data.status}</>
                );
            }
        },
        {
            colId: 'complianceDaysLeft',
            field: 'complianceDaysLeft',
            headerName: 'Days Left',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Days Left</SortableHeaderComponent>,
            width: 85,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            suppressSizeToFit: true
        },
        {
            colId: 'complianceDueDate',
            field: 'complianceDueDate',
            headerName: 'Due Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Due Date</SortableHeaderComponent>,
            width: 115,
            cellClass: 'follow-up-list-cell',
            headerClass: 'follow-up-list-header',
            sortable: true,
            suppressSizeToFit: true,
            comparator: Utility.dateComparator,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IFollowUpSurveyForList;

                if (data.isLate) {
                    return (
                        <Label bsStyle='danger' style={{ fontSize: '100%' }}>{data.complianceDueDate}</Label>
                    );
                }

                if (data.isAlmostLate) {
                    return (
                        <Label bsStyle='warning' style={{ fontSize: '100%' }}>{data.complianceDueDate}</Label>
                    );
                }

                return (
                    <Label bsStyle='info' style={{ fontSize: '100%' }}>{data.complianceDueDate}</Label>
                );
            }
        },
        {
            colId: 'actionMenu',
            cellRendererFramework: (params: ICellRendererParams) =>
                <div style={{ width: '100%', textAlign: 'center' }}>
                    <ActionButtonRenderer {...params}
                        pullRight={true}
                        listType={props.listType}
                        isAdmin={props.isAdmin}
                        isAdminLogin={props.isAdminLogin}
                        data={params.data}
                        onMenuToggle={handleMenuToggle}
                        onItemSelected={props.onItemSelected} />
                </div>,
            width: 80,
            cellClass: 'action-menu',
            headerClass: 'follow-up-list-header',
            suppressSizeToFit: true
        }
    ];

    const columnDefs = useMemo(() => cols, []);

    const list = props.list.length > 0 ? props.list : (props.isLoading ? null : []);
    const rowData = useMemo(() => list, [list]);
    const listTypeName = getListTypeName();

    return (
        <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            headerHeight={30}
            rowHeight={28}
            suppressCellSelection={true}
            overlayNoRowsTemplate={`No [${listTypeName}] surveys...`}
            overlayLoadingTemplate={`Loading [${listTypeName}] surveys...`}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onBodyScroll={onBodyScroll}
        />
    );
}

export default CommonList;